import React, { useEffect, useMemo, useRef, useState } from 'react';
import { lossesAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Loader2, Globe, CalendarDays, Package } from 'lucide-react';

const TYPE_COLS = [
    { key: 'dlc_qty', label: 'DLC', cls: 'text-yellow-600' },
    { key: 'veille_qty', label: 'Veille', cls: 'text-orange-600' },
    { key: 'jour_qty', label: 'Jour', cls: 'text-red-600' },
];
const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const WP_WEEK_W = 52;
const WP_WEEK_GAP = 2;

const toIso = (d) => d.toISOString().split('T')[0];
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
function mondayOf(dateIso) {
    const d = new Date(dateIso + 'T00:00:00');
    const dow = d.getDay();
    return toIso(addDays(d, dow === 0 ? -6 : 1 - dow));
}
const fmtDM = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

// ---- Sélecteur horizontal semaines/mois (comme l'écran Ventes) ----
function PeriodPicker({ startDate, endDate, onChange }) {
    const scrollerRef = useRef(null);

    const weeks = useMemo(() => {
        const today = new Date();
        let start = addDays(today, -400);
        start = new Date(mondayOf(toIso(start)) + 'T00:00:00');
        const end = new Date(mondayOf(toIso(today)) + 'T00:00:00');
        const ws = [];
        let cur = new Date(start);
        while (cur <= end) { ws.push(new Date(cur)); cur = addDays(cur, 7); }
        return ws;
    }, []);

    const monthGroups = useMemo(() => {
        const groups = [];
        let current = null;
        weeks.forEach((mon, i) => {
            const thu = addDays(mon, 3);
            const key = `${thu.getFullYear()}-${thu.getMonth() + 1}`;
            if (!current || current.key !== key) {
                current = { key, year: thu.getFullYear(), month: thu.getMonth() + 1, startIdx: i, nWeeks: 1 };
                groups.push(current);
            } else current.nWeeks += 1;
        });
        return groups;
    }, [weeks]);

    const selectedMonthKey = useMemo(() => {
        if (!startDate || !endDate || startDate.slice(8, 10) !== '01') return null;
        const [y, m] = startDate.split('-').map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        if (endDate !== `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`) return null;
        return `${y}-${m}`;
    }, [startDate, endDate]);

    const selMon = startDate ? mondayOf(startDate) : null;
    const selEndMon = endDate ? mondayOf(endDate) : null;
    const isSelected = (monIso) => selMon && selEndMon && monIso >= selMon && monIso <= selEndMon;

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el || !weeks.length) return;
        const targetIso = (startDate && mondayOf(startDate)) || mondayOf(toIso(new Date()));
        const idx = weeks.findIndex(w => toIso(w) === targetIso);
        if (idx < 0) return;
        el.scrollLeft = Math.max(0, (idx - 6) * (WP_WEEK_W + WP_WEEK_GAP));
    }, [weeks]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="flex items-center gap-1 bg-slate-50 rounded-lg border border-slate-200 p-1" data-testid="losses-period-picker">
            <button type="button" onClick={() => { if (scrollerRef.current) scrollerRef.current.scrollLeft -= 200; }} className="h-12 w-6 flex-shrink-0 text-slate-400 hover:text-slate-700">‹</button>
            <div ref={scrollerRef} className="flex-1 overflow-x-auto" style={{ scrollBehavior: 'smooth' }}>
                <div className="min-w-max">
                    <div className="flex" style={{ gap: `${WP_WEEK_GAP}px` }}>
                        {monthGroups.map(g => {
                            const width = g.nWeeks * WP_WEEK_W + (g.nWeeks - 1) * WP_WEEK_GAP;
                            const active = selectedMonthKey === g.key;
                            return (
                                <button key={g.key} type="button"
                                    onClick={() => {
                                        const lastDay = new Date(g.year, g.month, 0).getDate();
                                        onChange(`${g.year}-${String(g.month).padStart(2, '0')}-01`, `${g.year}-${String(g.month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`);
                                    }}
                                    className={`shrink-0 h-5 mb-0.5 rounded border text-[10px] font-semibold leading-none ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                                    style={{ width: `${width}px` }}
                                    data-testid={`losses-month-${g.key}`}>
                                    {MONTHS_FR[g.month - 1]}{g.month === 1 && <span className="ml-1 opacity-50">{g.year}</span>}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex" style={{ gap: `${WP_WEEK_GAP}px` }}>
                        {weeks.map((mon, i) => {
                            const monIso = toIso(mon);
                            const selected = isSelected(monIso);
                            return (
                                <button key={i} type="button"
                                    onClick={() => onChange(monIso, toIso(addDays(mon, 6)))}
                                    className={`shrink-0 h-7 rounded border text-[10px] font-semibold leading-none ${selected ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
                                    style={{ width: `${WP_WEEK_W}px` }}
                                    data-testid={`losses-week-${monIso}`}>
                                    {fmtDM(monIso)}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
            <button type="button" onClick={() => { if (scrollerRef.current) scrollerRef.current.scrollLeft += 200; }} className="h-12 w-6 flex-shrink-0 text-slate-400 hover:text-slate-700">›</button>
        </div>
    );
}

const Cells = ({ r, bold = false }) => (
    <>
        {TYPE_COLS.map(c => (
            <td key={c.key} className={`px-2 py-1.5 text-right tabular-nums ${bold ? 'font-bold' : ''} ${r[c.key] > 0 ? c.cls : 'text-slate-300'}`}>
                {Math.round(r[c.key] * 100) / 100}
            </td>
        ))}
        <td className={`px-2 py-1.5 text-right tabular-nums ${bold ? 'font-bold' : 'font-semibold'}`}>{Math.round(r.total_qty * 100) / 100}</td>
        <td className={`px-2 py-1.5 text-right tabular-nums ${bold ? 'font-bold' : 'font-semibold'}`}>{r.value ? formatCurrency(r.value) : '—'}</td>
    </>
);

function HeadRow({ label }) {
    return (
        <tr>
            <th className="px-2 py-2 text-left">{label}</th>
            {TYPE_COLS.map(c => <th key={c.key} className="px-2 py-2 text-right">{c.label}</th>)}
            <th className="px-2 py-2 text-right">Qté totale</th>
            <th className="px-2 py-2 text-right">Valeur</th>
        </tr>
    );
}

function SynthTable({ rows, totals, labelKey, labelHeader, testId }) {
    return (
        <div className="border rounded-lg overflow-x-auto bg-white" data-testid={testId}>
            <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500"><HeadRow label={labelHeader} /></thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-400">Aucune perte sur la période</td></tr>
                    )}
                    {rows.map((r, i) => (
                        <tr key={i} className="border-t">
                            <td className="px-2 py-1.5 font-medium">{r[labelKey]}</td>
                            <Cells r={r} />
                        </tr>
                    ))}
                </tbody>
                {rows.length > 0 && (
                    <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                        <tr>
                            <td className="px-2 py-2 font-bold">Total</td>
                            <Cells r={totals} bold />
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}

// ---- Vue par jour, regroupée par semaine avec total semaine ----
function DailyByWeekTable({ rows, totals, startDate, endDate }) {
    const weeks = useMemo(() => {
        const byDate = Object.fromEntries(rows.map(r => [r.date, r]));
        const out = [];
        let mon = mondayOf(startDate);
        const endMon = mondayOf(endDate);
        while (mon <= endMon) {
            const days = [];
            const wTotal = { dlc_qty: 0, veille_qty: 0, jour_qty: 0, total_qty: 0, value: 0 };
            for (let i = 0; i < 7; i++) {
                const iso = toIso(addDays(new Date(mon + 'T00:00:00'), i));
                if (iso < startDate || iso > endDate) continue;
                const r = byDate[iso] || { date: iso, dlc_qty: 0, veille_qty: 0, jour_qty: 0, total_qty: 0, value: 0 };
                days.push({ ...r, dow: i });
                TYPE_COLS.forEach(c => { wTotal[c.key] += r[c.key]; });
                wTotal.total_qty += r.total_qty;
                wTotal.value += r.value;
            }
            if (days.length) out.push({ mon, days, wTotal: { ...wTotal, value: Math.round(wTotal.value * 100) / 100 } });
            mon = toIso(addDays(new Date(mon + 'T00:00:00'), 7));
        }
        return out;
    }, [rows, startDate, endDate]);

    return (
        <div className="border rounded-lg overflow-x-auto bg-white" data-testid="synthesis-daily-table">
            <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500"><HeadRow label="Jour" /></thead>
                <tbody>
                    {weeks.length === 0 && (
                        <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-400">Aucune perte sur la période</td></tr>
                    )}
                    {weeks.map(w => (
                        <React.Fragment key={w.mon}>
                            <tr className="bg-slate-100/80 border-t">
                                <td colSpan={6} className="px-2 py-1 text-[10px] font-bold text-slate-500 uppercase tracking-wide">
                                    Semaine du {fmtDM(w.mon)} au {fmtDM(toIso(addDays(new Date(w.mon + 'T00:00:00'), 6)))}
                                </td>
                            </tr>
                            {w.days.map(d => (
                                <tr key={d.date} className={`border-t ${d.total_qty === 0 ? 'text-slate-300' : ''}`}>
                                    <td className="px-2 py-1.5 font-medium">
                                        <span className="text-slate-400 mr-1.5">{DAYS_FR[d.dow]}</span>{fmtDM(d.date)}
                                    </td>
                                    <Cells r={d} />
                                </tr>
                            ))}
                            <tr className="border-t bg-orange-50/70" data-testid={`week-total-${w.mon}`}>
                                <td className="px-2 py-1.5 font-bold text-orange-700">Total semaine</td>
                                <Cells r={w.wTotal} bold />
                            </tr>
                        </React.Fragment>
                    ))}
                </tbody>
                {weeks.length > 1 && (
                    <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                        <tr>
                            <td className="px-2 py-2 font-bold">Total période</td>
                            <Cells r={totals} bold />
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}

export default function LossesSynthesis({ siteId }) {
    const [range, setRange] = useState(() => {
        const mon = mondayOf(toIso(new Date()));
        return { start: mon, end: toIso(addDays(new Date(mon + 'T00:00:00'), 6)) };
    });
    const isGlobal = siteId === 'ALL';
    const [groupMode, setGroupMode] = useState('day'); // day | product
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!siteId) return;
        setLoading(true);
        const params = { start_date: range.start, end_date: range.end };
        if (!isGlobal) params.site_id = siteId;
        if (groupMode === 'day') params.group = 'day';
        lossesAPI.synthesis(params)
            .then(r => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [isGlobal, groupMode, siteId, range]);

    return (
        <div className="space-y-3" data-testid="losses-synthesis">
            <PeriodPicker startDate={range.start} endDate={range.end} onChange={(s, e) => setRange({ start: s, end: e })} />
            <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex bg-slate-200 rounded-lg p-0.5 gap-0.5">
                    <button type="button" onClick={() => setGroupMode('day')}
                        className={`px-3 h-7 rounded-md text-xs font-semibold flex items-center gap-1 ${groupMode === 'day' ? 'bg-white shadow' : 'text-slate-600'}`}
                        data-testid="synthesis-group-day"><CalendarDays className="w-3 h-3" /> Par jour</button>
                    <button type="button" onClick={() => setGroupMode('product')}
                        className={`px-3 h-7 rounded-md text-xs font-semibold flex items-center gap-1 ${groupMode === 'product' ? 'bg-white shadow' : 'text-slate-600'}`}
                        data-testid="synthesis-group-product"><Package className="w-3 h-3" /> {isGlobal ? 'Par site' : 'Par produit'}</button>
                </div>
                {isGlobal && (
                    <span className="text-[11px] font-semibold text-orange-600 flex items-center gap-1"><Globe className="w-3 h-3" /> Fuji Express — tous sites</span>
                )}
                <span className="text-[11px] text-slate-400">{fmtDM(range.start)} → {fmtDM(range.end)}</span>
                {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            </div>
            {data && (data.mode === 'day'
                ? <DailyByWeekTable rows={data.rows} totals={data.totals} startDate={range.start} endDate={range.end} />
                : data.mode === 'global'
                    ? <SynthTable rows={data.rows} totals={data.totals} labelKey="site_name" labelHeader="Site" testId="synthesis-global-table" />
                    : <SynthTable rows={data.rows} totals={data.totals} labelKey="libelle" labelHeader="Produit" testId="synthesis-site-table" />
            )}
        </div>
    );
}
